import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GoogleAnalytics = ({ trackingId = 'G-H6NLX7LXCZ' }) => {
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Analytics
    if (typeof window !== 'undefined') {
      // Load Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      gtag('js', new Date());
      gtag('config', trackingId, {
        send_page_view: false, // We'll send manually
        custom_map: {
          custom_parameter_1: 'page_category'
        }
      });

      // Enhanced E-commerce tracking setup
      gtag('config', trackingId, {
        enhanced_ecommerce: true
      });
    }
  }, [trackingId]);

  useEffect(() => {
    // Track page views on route changes
    if (typeof window.gtag !== 'undefined') {
      const pagePath = location.pathname + location.search;
      const pageTitle = document.title;
      
      // Enhanced page view tracking
      window.gtag('config', trackingId, {
        page_path: pagePath,
        page_title: pageTitle,
        page_location: window.location.href
      });

      // Custom page category tracking
      let pageCategory = 'general';
      if (pagePath.includes('/detalji/')) {
        pageCategory = 'product_detail';
      } else if (pagePath === '/') {
        pageCategory = 'homepage';
      } else if (pagePath === '/o-nama') {
        pageCategory = 'about';
      } else if (pagePath === '/kontakt') {
        pageCategory = 'contact';
      } else if (pagePath === '/bagis') {
        pageCategory = 'services';
      }

      // Send custom event with page category
      window.gtag('event', 'page_view_detailed', {
        page_category: pageCategory,
        page_path: pagePath,
        page_title: pageTitle
      });
    }
  }, [location, trackingId]);

  // Helper functions for tracking business events
  const trackBusinessEvents = () => {
    // Track phone clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('a[href^="tel:"]')) {
        window.gtag && window.gtag('event', 'phone_call', {
          event_category: 'Contact',
          event_label: 'Phone Click',
          value: 1
        });
      }
    });

    // Track email clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('a[href^="mailto:"]')) {
        window.gtag && window.gtag('event', 'email_click', {
          event_category: 'Contact',
          event_label: 'Email Click',
          value: 1
        });
      }
    });

    // Track social media clicks
    document.addEventListener('click', (e) => {
      const socialLink = e.target.closest('a[href*="facebook.com"], a[href*="instagram.com"], a[href*="youtube.com"]');
      if (socialLink) {
        const platform = socialLink.href.includes('facebook') ? 'Facebook' :
                         socialLink.href.includes('instagram') ? 'Instagram' : 'YouTube';
        
        window.gtag && window.gtag('event', 'social_click', {
          event_category: 'Social',
          event_label: platform,
          value: 1
        });
      }
    });

    // Track form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.closest('form')) {
        window.gtag && window.gtag('event', 'form_submit', {
          event_category: 'Lead Generation',
          event_label: 'Contact Form',
          value: 5
        });
      }
    });

    // Track scroll depth
    let maxScroll = 0;
    const trackScrollDepth = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        window.gtag && window.gtag('event', 'scroll_depth', {
          event_category: 'Engagement',
          event_label: `${scrollPercent}%`,
          value: scrollPercent
        });
      }
    };

    window.addEventListener('scroll', trackScrollDepth);
  };

  useEffect(() => {
    trackBusinessEvents();
  }, []);

  return null; // This component doesn't render anything
};

// Helper functions for manual event tracking
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, parameters);
  }
};

export const trackVehicleView = (vehicleId, vehicleName, category = 'Vehicle') => {
  trackEvent('view_item', {
    item_id: vehicleId,
    item_name: vehicleName,
    item_category: category,
    event_category: 'E-commerce',
    value: 1
  });
};

export const trackVehicleInterest = (vehicleId, vehicleName, action = 'interest') => {
  trackEvent('vehicle_interest', {
    item_id: vehicleId,
    item_name: vehicleName,
    action: action,
    event_category: 'Lead Generation',
    value: 3
  });
};

export const trackSearchQuery = (searchTerm, resultsCount) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    event_category: 'Search',
    value: 1
  });
};

export const trackBusinessGoal = (goalType, goalValue = 1) => {
  trackEvent('business_goal', {
    goal_type: goalType,
    event_category: 'Business Goals',
    value: goalValue
  });
};

export default GoogleAnalytics;
